import * as React from 'react';
import GenericHoc from 'src/components/hoc/GenericHoc';
import SI from './document-request.si';
import VGM from './document-request.vgm';
import Form13 from './document-request.form13';
import { HocOptions } from 'src/components/hoc/GenericHoc.types';
import { StylesProps } from 'src/Library/Types';
import { documentRequestStyles } from './document-request.styles';
import { documentRequestTabs, RequestStatus } from './document-request.constants';
import { bindActionCreators } from 'redux';
import { fetchAllSEDocuments, fetchPendingContainerCount } from 'src/actions/shipmentExecutionActions';
import GetExecutionData from 'src/dto/response/getExecution';
import { ExecutionDetails } from '../SEDetails/shipment-details.types';
import { fetchForm13OrgAssignee } from 'src/api/shipmentExecution';
import { getOrgPrettyName } from 'src/api/utils';
import { isValidString } from 'src/Library/Helper';
import { useGenericState } from 'src/Library/UseGenericState';
import FileVGMModal from '../SETasks/SETaskList/FileVGMModal';
import isEmpty from 'lodash/isEmpty';
import UploadSIModal from '../SETasks/SETaskList/UploadSIModal';
import UploadSISuccessModal from '../SETasks/SETaskList/UploadSISuccessModal';
import Form13UploadModal from '../SETasks/SETaskList/Form13UploadModal';
import Form13RequestDetailsForm from '../SETasks/SETaskList/Form13RequestDetailsForm';
import FileOdexSuccessModal from '../SETasks/SETaskList/FileOdexSuccessModal';
import { ValidMilestoneCodes } from '../../SE.constants';
import Tooltip from 'antd/lib/tooltip';

interface DocumentRequestProps extends StylesProps<typeof documentRequestStyles> {
  inttraCode;
  vgmRequests;
  siRequestData;
  form13Requests;
  isInttraSIAllowed;
  shippingInstructions;
  odexVgmReferenceNumber;
  gotoTaskTab: () => void;
  showInttraShippingInstructions;
  selectedShipment: GetExecutionData;
  shipmentDetails: ExecutionDetails | undefined;
  activeSubTab: keyof typeof documentRequestTabs; 
  currentShipmentMilestone: keyof typeof ValidMilestoneCodes;
  fetchAllSEDocuments: (shipmentId: string | number, isDraft?: boolean, tabToOpen?: string) => void;
}

interface StateProps {
  odexRequestBody?;
  retryRequestData?;
  isAmend?: boolean;
  odexSuccessResponse?;
  odexFormType?: string;
  isRetryError?: boolean;
  updatedContainerStatus?;
  isResubmitRequest?: boolean;
  form13OrganisationId?: string;
  isFileVgmModalVisible?: boolean;
  isFetchingShippingData?: boolean;
  isUploadSIModalVisible?: boolean;
  isSISuccessModalVisible?: boolean;
  isFetchingForm13Assignee?: boolean;
  isForm13UploadModalVisible?: boolean;
  isOdexRequestSuccessModalVisible?: boolean;
  isForm13RequestDetailsModalVisible?: boolean;
}

const DocumentRequest = (props: DocumentRequestProps) => {
  const {
    classes,
    inttraCode,
    gotoTaskTab,
    activeSubTab,
    siRequestData,
    shipmentDetails,
    selectedShipment,
    isInttraSIAllowed,
    fetchAllSEDocuments,
    odexVgmReferenceNumber,
    currentShipmentMilestone,
    showInttraShippingInstructions,
  } = props;

  const [active, setActive] = React.useState<keyof typeof documentRequestTabs>('vgm');
  const [state, setState] = useGenericState<StateProps>({});

  const {
    isAmend,
    isRetryError,
    odexFormType,
    odexRequestBody,
    retryRequestData,
    isResubmitRequest,
    odexSuccessResponse,
    form13OrganisationId,
    isFileVgmModalVisible,
    isUploadSIModalVisible,
    updatedContainerStatus,
    isSISuccessModalVisible,
    isForm13UploadModalVisible,
    isOdexRequestSuccessModalVisible,
    isForm13RequestDetailsModalVisible,
  } = state;

  const onHold = currentShipmentMilestone === 'ON_HOLD' || currentShipmentMilestone === 'DELETED';

  const carrierAndPortOdexCodeCheck = isValidString(shipmentDetails?.carrierOdexLinerId) &&
      ((isValidString(shipmentDetails?.originPolCode) &&
      isValidString(shipmentDetails?.originPolOdexCode)) ||
      (isValidString(shipmentDetails?.originPortCode) &&
      isValidString(shipmentDetails?.originPortOdexCode)));
  const showRequestForm13Button = carrierAndPortOdexCodeCheck;

  const currentOrgId = getOrgPrettyName();
  const showForm13 = (form13OrganisationId === currentOrgId) && showRequestForm13Button;
  const showFileVGMOption = odexVgmReferenceNumber && carrierAndPortOdexCodeCheck;
  const showUploadSIOption = showInttraShippingInstructions && inttraCode && isInttraSIAllowed;

  React.useEffect(() => {
    if (!activeSubTab) {
      return;
    }
    setActive(activeSubTab);
  }, [activeSubTab]);

  React.useEffect(() => {
    if (!selectedShipment?.id) {
      return;
    }
    fetchAllSEDocuments(selectedShipment.id, false, active?.toUpperCase());
    fetchForm13TaskAssignee();
  }, [selectedShipment, active]);

  const getStatusTitleAndColor = (status: string) => {
    switch (status) {
      case RequestStatus.ACCEPTED: return {
        color: '#5ADA13', label: 'Accepted'
      };
      case RequestStatus.REQUESTED: return {
        color: '#ED8732', label: 'Requested'
      };
      case RequestStatus.REJECTED: return {
        color: '#F5222D', label: 'Rejected'
      };
      default: return {};
    }
  }

  const renderStatusDot = (status: string) => {
    const { label, color } = getStatusTitleAndColor(status); 

    return (
      <Tooltip title={label}>
      <div
        className={classes.statusDot}
        style={{ backgroundColor: color }} />
      </Tooltip>
    );
  }

  const reload = () => {
    fetchAllSEDocuments(selectedShipment.id, false, active?.toUpperCase());
  };

  const updateContainerStatus = (containerDetails) => {
    setState({
      updatedContainerStatus: {
        ...(updatedContainerStatus || {}),
        ...containerDetails,
      }
    });
  };

  const fetchForm13TaskAssignee = async () => {
    setState({ isFetchingForm13Assignee: true });
    const response = await fetchForm13OrgAssignee(shipmentDetails?.id);
    if (response.isSuccess) {
      setState({ form13OrganisationId: response.data?.id });
    } else {
      // message.error(response.errorMessage);
    }
    setState({ isFetchingForm13Assignee: false });
  }

  const renderHeader = () => {
    return <div className={classes.header}>
      {Object.keys(documentRequestTabs)
        .map((key: keyof typeof documentRequestTabs) => {
          return (
            <div
              key={key}
              style={{
                borderBottom: active === key ? '2px solid #006EC3' : 'none',
                fontWeight: active === key ? 'bold' : 'inherit',
              }}
              onClick={() => setActive(key)}
              className={classes.tab}>
              {documentRequestTabs[key]}
            </div>
          );
        })}
    </div>
  };

  const renderSI = () => {
    if (active !== 'si') {
      return null;
    }
    return <SI
      reload={reload}
      shipmentDetails={shipmentDetails}
      renderStatusDot={renderStatusDot}
      selectedShipment={selectedShipment}
      setUploadSIModalStates={setModalStates}
      actionsEnabled={showUploadSIOption && !onHold}
    />;
  };

  const setModalStates = (props) : void => {
    setState({
      ...props
    })
  }

  const renderVGM = () => {
    if (active !== 'vgm') {
      return null;
    }
    return <VGM
      shipmentDetails={shipmentDetails}
      selectedShipment={selectedShipment}
      setFileVGMModalStates={setModalStates}
      updateContainerStatus={updateContainerStatus}
      actionsEnabled={showFileVGMOption && !onHold}
      updatedContainerStatus={updatedContainerStatus}
    />;
  };

  const renderForm13 = () => {
    if (active !== 'form13') {
      return null;
    }
    return <Form13
      shipmentDetails={shipmentDetails}
      setForm13ModalStates={setModalStates}
      actionsEnabled={showForm13 && !onHold}
      updateContainerStatus={updateContainerStatus}
      updatedContainerStatus={updatedContainerStatus}
    />;
  };

  const renderFileVGMModal = () : React.ReactElement => {
    return (
      <FileVGMModal
        isVisible={isFileVgmModalVisible}
        onCancel={() => setState({ isFileVgmModalVisible: false, isRetryError: false, odexRequestBody: undefined })}
        shipmentData={shipmentDetails}
        taskId={''}
        gotoDocumentRequest={() => {
          reload();
          setState({ isFileVgmModalVisible: false })
        }}
        onSaveDraft={gotoTaskTab}
        isDocumentRequestTab={true}
        isReSubmitting={isRetryError}
        retryRequestData={isRetryError ? retryRequestData : undefined}
        onSubmit={(success, odexRequestBody, odexSuccessResponse) => {
          if (success) {
            fetchPendingContainerCount({ shipmentId: shipmentDetails?.id, });
            setState({
              odexRequestBody,
              odexSuccessResponse,
              isOdexRequestSuccessModalVisible: true,
              isFileVgmModalVisible: false,
              isRetryError: false,
            });
          } else {
            setState({ isFileVgmModalVisible: false, isRetryError: false, odexRequestBody: undefined });
          }
        }}
        isRetryRequest={!isEmpty(odexRequestBody)}
      />
    );
  }

  const renderUploadSIModal = (): React.ReactElement => {
    if (!shipmentDetails?.id) {
      return <></>;
    }

    return (
      <UploadSIModal
        setDocumentTab={() => {
          reload();
          setState({ isUploadSIModalVisible: false })
        }}
        isDocumentRequestTab={true}
        onSaveDraft={() => {
          gotoTaskTab();
        }}
        amendData={isAmend || isRetryError ? siRequestData : undefined}
        isVisible={isUploadSIModalVisible}
        onCancel={() =>
          setState({ isUploadSIModalVisible: false, isAmend: false, isRetryError: false })
        }
        shipmentData={shipmentDetails}
        isAmend={isAmend}
        isRetryError={isRetryError}
        onSubmit={(success) => {
          if (success) {
            fetchPendingContainerCount({ shipmentId: shipmentDetails?.id });
            setState({
              isSISuccessModalVisible: true,
              isUploadSIModalVisible: false,
            });
          } else {
            setState({ isUploadSIModalVisible: false });
          }
        }}
      />
    );
  };

  const renderUploadSISuccessModal = (): React.ReactElement => {
    if (!shipmentDetails?.id) {
      return <></>;
    }

    return (
      <UploadSISuccessModal
        shipmentId={shipmentDetails?.id}
        taskId={''}
        isVisible={isSISuccessModalVisible}
        openDocumentTab={() => {
          reload();
          setState({ isSISuccessModalVisible: false })
        }}
        onOkay={() => {
          setState({ isSISuccessModalVisible: false, isAmend: false, isRetryError: false });
        }}
        isAmend={isAmend}
        isRetryError={isRetryError}
        handleCreateNewRequest={() => setState({ isUploadSIModalVisible: true })}
      />
    );
  };

  const handleCancelForm13UploadModal = () => {
    setState({
      isForm13UploadModalVisible: false,
      isRetryError: false,
      odexRequestBody: undefined,
      isForm13RequestDetailsModalVisible: false,
      isResubmitRequest: false,
    });
  }

  const renderForm13UploadModal = (): React.ReactElement => {
    return (
      <Form13UploadModal
        isVisible={isForm13UploadModalVisible}
        handleCancel={handleCancelForm13UploadModal}
        isDocumentRequestTab={true}
        shipmentData={shipmentDetails}
        setDocumentTab={() => {
          reload();
          handleCancelForm13UploadModal();
        }}
        isRetryRequest={!isEmpty(odexRequestBody)}
        retryRequestData={isRetryError ? retryRequestData : undefined}
        onSaveDraft={gotoTaskTab}
        onSubmit={(success, requestBody, successResponse) => {
          if (success) {
            fetchPendingContainerCount({ shipmentId: shipmentDetails?.id, });
            setState({
              odexRequestBody: requestBody,
              odexSuccessResponse: successResponse,
              isOdexRequestSuccessModalVisible: true,
              isForm13RequestDetailsModalVisible: false,
              isRetryError: false,
              odexFormType: 'FORM13',
              isForm13UploadModalVisible: false,
              isResubmitRequest: false,
            });
          } else {
            setState({
              isForm13RequestDetailsModalVisible: false,
              isRetryError: false,
              odexRequestBody: undefined,
              isForm13UploadModalVisible: false,
              isResubmitRequest: false,
            });
          }
        }}
        isResubmitRequest={isResubmitRequest}
      />
    );
  };

  const renderForm13RequestDetailsModal = (): React.ReactElement => {
    return (
      <Form13RequestDetailsForm
        isVisible={isForm13RequestDetailsModalVisible}
        handleCancel={() => setState({ isForm13RequestDetailsModalVisible: false })}
        shipmentData={shipmentDetails}
        setDocumentTab={() => setState({ isForm13RequestDetailsModalVisible: false })}
        openForm13Modal={() => setState({ isForm13UploadModalVisible: true })}
      />
    );
  };

  const renderOdexSuccessModal = (): React.ReactElement => {
    const isForm13 = odexFormType === 'FORM13';
    if (!shipmentDetails?.id) {
      return <></>;
    }
    return (
      <FileOdexSuccessModal
        shipmentId={shipmentDetails?.id}
        onSaveDraft={() => {
          setState({ isOdexRequestSuccessModalVisible: false })
          fetchAllSEDocuments(shipmentDetails?.id, true);
        }}
        isDocumentRequestTab={true}
        isVisible={isOdexRequestSuccessModalVisible}
        openDocumentTab={() => setState({ isOdexRequestSuccessModalVisible: false })}
        requestBody={odexRequestBody}
        successResponse={odexSuccessResponse}
        handleCreateNewRequest={() => setState({ ...(isForm13 ? {
          isForm13UploadModalVisible : true,
        }
      :
        {
          isFileVgmModalVisible: true
        }),
        odexRequestBody: undefined,
        })}
        onOkay={(isRetryError, isViewRequest) => {
          if (!isViewRequest) {
            fetchAllSEDocuments(shipmentDetails?.id);
          }
          if (isRetryError) {
            setState({
              isOdexRequestSuccessModalVisible: false,
              isRetryError: true,
              odexFormType: undefined,
              ...(isForm13 ? {
                isForm13UploadModalVisible : true
              }
              :
              {
                isFileVgmModalVisible: true
              })
            });
          } else {
            setState({ isOdexRequestSuccessModalVisible: false, isRetryError: false, odexFormType: undefined });
          }
        }}
        handleRetryRequest={(retryRequestData => setState({ retryRequestData }))}
        openTasksTab={gotoTaskTab}
        formType={odexFormType}
      />
    );
  };

  return <div className={classes.main}>
    {renderHeader()}
    {renderSI()}
    {renderVGM()}
    {renderForm13()}
    {isSISuccessModalVisible && renderUploadSISuccessModal()}
    {isUploadSIModalVisible && renderUploadSIModal()}
    {isFileVgmModalVisible && renderFileVGMModal()}
    {isForm13UploadModalVisible && renderForm13UploadModal()}
    {isForm13RequestDetailsModalVisible && renderForm13RequestDetailsModal()}
    {isOdexRequestSuccessModalVisible && renderOdexSuccessModal()}
  </div>;
}
 

const mapStateToProps = ({ shipmentExecutionReducer, masterData }) => ({
  inttraCode: masterData.inttraCode,
  vgmRequests: shipmentExecutionReducer.vgmRequests,
  siRequestData: shipmentExecutionReducer.siRequestData,
  form13Requests: shipmentExecutionReducer.form13Requests,
  odexVgmReferenceNumber: masterData.odexVgmReferenceNumber,
  shippingInstructions: shipmentExecutionReducer.shippingInstructions,
  showInttraShippingInstructions: masterData.modules.show_inttra_shipping_instructions,
});

const mapDispatchToProps = dispatch => bindActionCreators({
  fetchAllSEDocuments,
  fetchPendingContainerCount,
}, dispatch);

const options: HocOptions = {
  connectRedux: {
    useRedux: true,
    mapStateToProps,
    mapDispatchToProps,
  },
  connectJss: {
    useJss: true,
    styleSheet: documentRequestStyles,
  },
};

const DocumentReq = GenericHoc(options)(DocumentRequest);
export default DocumentReq;
